import React, { useEffect, useCallback } from 'react';
import { useGlobalData } from '../context/GlobalDataContext';
import { useNavigate } from 'react-router-dom';
import NotificationPanel from './NotificationPanel';

import './NotificationPanelContainer.css';
import axios from 'axios';
import { getEligibilityCriterias, getJobs } from '../api/api';

const NotificationPanelContainer = () => {
  const navigate = useNavigate();
  const { setNotifications, notifications = [], error, setError, setProfile, profile } = useGlobalData();

  const hasFetchedRef = React.useRef(false);

  // Role check from JWT
  useEffect(() => {
    let userRole = '';
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userRole = payload.role || '';
      }
    } catch (e) {}
    if (userRole !== 'user') {
      navigate('/publicuser/login', { replace: true });
    }
  }, [navigate]);

  const fetchAll = useCallback(
    async (force = false) => {
      // ✅ Guard: same mount me repeat call stop
      if (!force && hasFetchedRef.current) return;

      // ✅ Guard: global notifications already present ho to remount par refetch skip
      if (!force && Array.isArray(notifications) && notifications.length > 0) {
        hasFetchedRef.current = true;
        return;
      }

      const loginEmail = localStorage.getItem('userEmail');
      const loginMobile = localStorage.getItem('userMobile');
      if (!loginEmail && !loginMobile) {
        hasFetchedRef.current = true;
        return;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        let user = profile;

        if (profile === null) {
          const profileRes = await axios.get(`${apiUrl}/publicuser/profile`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          user = profileRes.data.user;
          setProfile(prev => ({ ...prev, ...user }));
        }

        const params = {};
        if (loginEmail) params.email = loginEmail;
        if (loginMobile) params.mobile = loginMobile;

        const res = await axios.get(`${apiUrl}/publicuser/notifications`, {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        let notifs = res.data.notifications || [];

        const jobsRes = await getJobs();
        const jobs = jobsRes.data || [];

        const eligRes = await getEligibilityCriterias();
        let criterias = eligRes.data || [];
        criterias = criterias.filter(c => c.status === 'approved');

        const educationGroups = {
          school: ['10th', '12th', 'Diploma'],
          ug: ['B.A.', 'B.Sc.', 'B.Com.', 'B.Tech', 'BCA', 'BBA'],
          pg: ['M.A.', 'M.Sc.', 'M.Com.', 'M.Tech', 'MBA', 'MCA'],
          doctorate: ['PhD']
        };
        const groupOrder = ['school', 'ug', 'pg', 'doctorate'];

        function getUserGroup(education) {
          for (const group of groupOrder) {
            if (educationGroups[group].includes(education)) return group;
          }
          return null;
        }

        const eligibleCriterias = criterias.filter(criteria => {
          if (!criteria.education || !criteria.location || !criteria.minAge || !criteria.maxAge) return false;
          if (!user?.education || !user?.location || !user?.age) return false;

          const userGroup = getUserGroup(user.education);
          const requiredGroup = criteria.education;
          const userGroupIndex = groupOrder.indexOf(userGroup);
          const requiredGroupIndex = groupOrder.indexOf(requiredGroup);

          const eduMatch =
            userGroupIndex !== -1 &&
            requiredGroupIndex !== -1 &&
            userGroupIndex >= requiredGroupIndex;

          const locMatch = criteria.location === 'India' ? true : criteria.location === user.location;

          const age = Number(user.age);
          const minAge = Number(criteria.minAge);
          const maxAge = Number(criteria.maxAge);
          const ageMatch = age >= minAge && age <= maxAge;

          const genderMatch =
            (criteria.gender === 'all' || !criteria.gender)
              ? true
              : (user.gender && criteria.gender === user.gender);

          return eduMatch && locMatch && ageMatch && genderMatch;
        });

        notifs = notifs.filter(n => n.type !== 'eligibility-match');

        eligibleCriterias.forEach(criteria => {
          const already = notifs.some(
            n => n.type === 'eligibility-match' && n.jobTitle === criteria.jobTitle
          );
          if (!already) {
            const matchedJob = jobs.find(j => criteria.jobTitle.startsWith(j.title));
            notifs = [
              {
                type: 'eligibility-match',
                title: 'Eligibility Matched!',
                message: `🎉 Your profile matches the eligibility for "${criteria.jobTitle}".`,
                jobTitle: criteria.jobTitle,
                jobSlug: matchedJob ? matchedJob.slug : undefined,
                unread: true,
                time: criteria.createdAt
                  ? new Date(criteria.createdAt).toLocaleString()
                  : new Date().toLocaleString()
              },
              ...notifs
            ];
          }
        });

        setNotifications(notifs);
        hasFetchedRef.current = true; // ✅ mark fetched
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch notifications');
        hasFetchedRef.current = true; // ✅ avoid endless retries on remount
      }
    },
    [notifications, profile, setError, setNotifications, setProfile]
  );

  useEffect(() => {
    fetchAll(false);

    // ✅ targeted refresh
    const handler = (e) => {
      const resource = e?.detail?.resource;
      if (!resource || ['notifications', 'profile', 'eligibility', 'jobs'].includes(resource)) {
        fetchAll(true);
      }
    };

    window.addEventListener('dataChanged', handler);
    return () => window.removeEventListener('dataChanged', handler);
  }, [fetchAll]);

  if (profile === null) {
    return <div style={{ textAlign: 'center', margin: '2rem', color: '#888' }}>Loading profile...</div>;
  }

  return (
    <div>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <NotificationPanel onRefresh={() => fetchAll(true)} />
    </div>
  );
};

export default NotificationPanelContainer;